import { useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, Check, History, Loader, Pencil, Trash2, UserMinus, UserPlus, Users, X } from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { ConfirmationModal } from '../ConfirmationModal';
import { EmptyState } from '../ui/EmptyState';
import { SectionHeader } from '../ui/SectionHeader';
import { DiscordMemberPicker, type PickedDiscordMember } from './DiscordMemberPicker';
import { PartnerAvatar } from './PartnerAvatar';
import { PartnerAccessBadge, PartnerTierBadge } from './PartnerPhaseBadge';
import { PARTNER_TIERS, type DiscordGuildMember, type PartnerMember, type PartnerTier } from '../../types/partners';
import { cn } from '../../utils/cn';
import { memberName, discordName } from '../../utils/partnerLinks';
import { tierLabel } from '../../utils/partnerStatus';
import { describeSyncOutcome, type PartnerNotice } from '../../utils/partnerSync';

interface PartnerTeamSectionProps {
  creatorId: string;
  creatorName: string;
  members: PartnerMember[];
  /** Whether Mr. Roboto manages Discord for this partner (changes reach Discord only then). */
  managed: boolean;
  canEdit: boolean;
  onChanged: () => void | Promise<void>;
  onNotice: (notice: PartnerNotice) => void;
}

const ago = (ms: number | null | undefined) => (ms ? formatDistanceToNow(new Date(ms), { addSuffix: true }) : null);

const TierSelect = ({ value, onChange, disabled, id, className }: { value: PartnerTier; onChange: (tier: PartnerTier) => void; disabled?: boolean; id?: string; className?: string }) => (
  <select
    id={id}
    className={cn('glass-select', className)}
    value={value}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value as PartnerTier)}
    aria-label="Tier"
  >
    {PARTNER_TIERS.map((tier) => (
      <option key={tier} value={tier}>
        {tierLabel(tier).label}
      </option>
    ))}
  </select>
);

/**
 * The partner's team: who is on it, at which tier, and whether Discord has
 * caught up. Argus is the one place the team is kept; Mr. Roboto keeps the
 * channel and roles matching it.
 */
export const PartnerTeamSection = ({ creatorId, creatorName, members: stored, managed, canEdit, onChanged, onNotice }: PartnerTeamSectionProps) => {
  // Mr. Roboto fills in names once it sees someone. Until then, ask it once for
  // the people Argus has no name for, so the table does not read as bare IDs.
  const [looked, setLooked] = useState<Record<string, DiscordGuildMember>>({});
  const asked = useRef(new Set<string>());
  useEffect(() => {
    const missing = stored
      .filter((m) => !m.discord_display_name && !m.discord_username && !asked.current.has(m.discord_id))
      .map((m) => m.discord_id)
      .slice(0, 50);
    if (missing.length === 0) return;
    const askedIds = asked.current;
    missing.forEach((id) => askedIds.add(id));
    let cancelled = false;
    let settled = false;
    partnersApi
      .discordMembers({ ids: missing })
      .then((found) => {
        if (!cancelled) setLooked((prev) => ({ ...prev, ...Object.fromEntries(found.filter((m) => m.in_guild).map((m) => [m.id, m])) }));
      })
      .catch(() => {
        // Names are a nicety; the IDs still identify everyone.
      })
      .finally(() => {
        settled = true;
      });
    return () => {
      cancelled = true;
      // An answer that never arrived may be asked for again on the next run.
      if (!settled) missing.forEach((id) => askedIds.delete(id));
    };
  }, [stored]);

  const members = useMemo(
    () =>
      stored.map((m) => {
        const seen = looked[m.discord_id];
        if (!seen || m.discord_display_name || m.discord_username) return m;
        return { ...m, discord_display_name: seen.display_name, discord_username: seen.username, discord_avatar_url: m.discord_avatar_url ?? seen.avatar_url };
      }),
    [stored, looked],
  );
  const active = useMemo(() => members.filter((m) => m.status === 'active'), [members]);
  const removed = useMemo(() => members.filter((m) => m.status === 'removed'), [members]);
  const takenIds = useMemo(() => new Set(active.map((m) => m.discord_id)), [active]);

  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<PickedDiscordMember | null>(null);
  const [tier, setTier] = useState<PartnerTier>('contributor');
  const [note, setNote] = useState('');
  const [addError, setAddError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<{ id: string; text: string } | null>(null);
  const [removing, setRemoving] = useState<PartnerMember | null>(null);
  const [forgetting, setForgetting] = useState<PartnerMember | null>(null);

  const resetAdd = () => {
    setAdding(false);
    setPicked(null);
    setTier('contributor');
    setNote('');
    setAddError(null);
  };

  const fail = (error: unknown, fallback: string) => onNotice({ tone: 'danger', text: error instanceof Error ? error.message : fallback });

  const add = async () => {
    if (!picked) return;
    setSubmitting(true);
    setAddError(null);
    try {
      const res = await partnersApi.addMember(creatorId, { discord_id: picked.id, tier, note: note.trim() || null });
      const name = discordName({ display_name: res.member.discord_display_name, username: res.member.discord_username }, picked.display_name || picked.username || picked.id);
      onNotice(describeSyncOutcome(res.sync, `Added ${name} to ${creatorName}’s team as a ${tierLabel(tier).label}.`, managed));
      resetAdd();
      await onChanged();
    } catch (error) {
      setAddError(error instanceof Error ? error.message : 'They could not be added.');
    } finally {
      setSubmitting(false);
    }
  };

  const changeTier = async (member: PartnerMember, next: PartnerTier) => {
    if (next === member.tier) return;
    setBusyId(member.id);
    try {
      const res = await partnersApi.updateMember(creatorId, member.id, { tier: next });
      onNotice(describeSyncOutcome(res.sync, `${memberName(member)} is now a ${tierLabel(next).label}.`, managed));
      await onChanged();
    } catch (error) {
      fail(error, 'The tier could not be changed.');
    } finally {
      setBusyId(null);
    }
  };

  const saveNote = async () => {
    if (!editingNote) return;
    const member = active.find((m) => m.id === editingNote.id);
    if (!member) return;
    setBusyId(member.id);
    try {
      await partnersApi.updateMember(creatorId, member.id, { note: editingNote.text.trim() || null });
      setEditingNote(null);
      onNotice({ tone: 'success', text: `Saved the note on ${memberName(member)}.` });
      await onChanged();
    } catch (error) {
      fail(error, 'The note could not be saved.');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async () => {
    if (!removing) return;
    const member = removing;
    setBusyId(member.id);
    try {
      const res = await partnersApi.removeMember(creatorId, member.id);
      onNotice(describeSyncOutcome(res.sync, `Removed ${memberName(member)} from the team.`, managed));
      await onChanged();
    } catch (error) {
      fail(error, 'They could not be removed.');
    } finally {
      setBusyId(null);
      setRemoving(null);
    }
  };

  const forget = async () => {
    if (!forgetting) return;
    const member = forgetting;
    setBusyId(member.id);
    try {
      await partnersApi.forgetMember(creatorId, member.id);
      onNotice({ tone: 'success', text: `Forgot ${memberName(member)}. Their removed rows on this team are gone from Argus.` });
      await onChanged();
    } catch (error) {
      fail(error, 'They could not be forgotten.');
    } finally {
      setBusyId(null);
      setForgetting(null);
    }
  };

  const noteCell = (member: PartnerMember) => {
    if (editingNote?.id === member.id) {
      return (
        <div className="flex items-center gap-1.5">
          <input
            className="glass-input min-h-[32px] py-1 flex-1 min-w-0 text-sm"
            value={editingNote.text}
            maxLength={200}
            autoFocus
            onChange={(e) => setEditingNote({ id: member.id, text: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void saveNote();
              if (e.key === 'Escape') setEditingNote(null);
            }}
            aria-label={`Note on ${memberName(member)}`}
          />
          <button type="button" className="btn-icon btn-icon-sm" onClick={() => void saveNote()} disabled={busyId === member.id} title="Save note" aria-label="Save note">
            {busyId === member.id ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </button>
          <button type="button" className="btn-icon btn-icon-sm" onClick={() => setEditingNote(null)} title="Cancel" aria-label="Cancel">
            <X className="w-4 h-4" />
          </button>
        </div>
      );
    }
    return member.note ? <span className="text-sm text-glass-300 whitespace-pre-wrap break-words">{member.note}</span> : <span className="text-xs text-glass-500">No note</span>;
  };

  const actions = (member: PartnerMember) =>
    canEdit && (
      <div className="flex items-center justify-end gap-0.5">
        <button
          type="button"
          className="btn-icon btn-icon-sm"
          onClick={() => setEditingNote({ id: member.id, text: member.note ?? '' })}
          disabled={busyId === member.id}
          title="Edit note"
          aria-label={`Edit the note on ${memberName(member)}`}
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          type="button"
          className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
          onClick={() => setRemoving(member)}
          disabled={busyId === member.id}
          title="Remove from the team"
          aria-label={`Remove ${memberName(member)} from the team`}
        >
          <UserMinus className="w-4 h-4" />
        </button>
      </div>
    );

  const who = (member: PartnerMember) => {
    const name = memberName(member);
    const named = name !== member.discord_id;
    return (
      <div className="flex items-center gap-2.5 min-w-0">
        <PartnerAvatar avatarUrl={member.discord_avatar_url} name={name} size="md" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-white truncate">{named ? name : 'Discord member'}</p>
          <p className="text-[11px] font-mono text-glass-500 truncate" title={`Discord ID ${member.discord_id}`}>
            {member.discord_username ? `@${member.discord_username} · ` : ''}
            {member.discord_id}
          </p>
        </div>
      </div>
    );
  };

  const access = (member: PartnerMember) => (
    <div className="space-y-1">
      <PartnerAccessBadge state={member.access_state} detail={member.access_detail} />
      {member.access_detail && <p className="text-xs text-glass-400 max-w-[16rem]">{member.access_detail}</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Team"
        icon={Users}
        count={active.length}
        actions={
          canEdit &&
          !adding && (
            <button type="button" className="btn-primary" data-tour="partner-add-member" onClick={() => setAdding(true)}>
              <UserPlus className="w-4 h-4" />
              Add to team
            </button>
          )
        }
      />
      <p className="text-sm text-glass-400">
        Partnered Developers get the Partnered Modpack Developer role, which opens the stage, and can share server packs with us. Contributors get
        the acknowledgement role. Everyone here gets the partner channel. Someone holds one tier at a time.
      </p>

      {adding && canEdit && (
        <div className="subcard p-3 md:p-4 space-y-3">
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem]">
            <DiscordMemberPicker value={picked} onChange={setPicked} takenIds={takenIds} disabled={submitting} />
            <div>
              <label htmlFor="partner-add-tier" className="block text-xs font-medium text-glass-400 mb-1.5">
                Tier
              </label>
              <TierSelect id="partner-add-tier" value={tier} onChange={setTier} disabled={submitting} className="w-full" />
              <p className="text-xs text-glass-500 mt-1">{tierLabel(tier).hint}</p>
            </div>
          </div>
          <div>
            <label htmlFor="partner-add-note" className="block text-xs font-medium text-glass-400 mb-1.5">
              Note for staff (optional)
            </label>
            <input
              id="partner-add-note"
              className="glass-input w-full"
              value={note}
              maxLength={200}
              placeholder="e.g. Lead developer, prefers DMs for release dates"
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
            />
          </div>
          {addError && <div className="alert alert-danger" role="alert"><AlertCircle className="w-4 h-4" />{addError}</div>}
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button type="button" className="glass-button-secondary" onClick={resetAdd} disabled={submitting}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={() => void add()} disabled={!picked || submitting}>
              {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Add as {tierLabel(tier).label}
            </button>
          </div>
        </div>
      )}

      {active.length === 0 ? (
        <EmptyState
          bare
          compact
          icon={Users}
          title="Nobody is on this team yet"
          description={
            canEdit
              ? 'Add the people who make this partner’s packs. If they already have a hand-made channel, link it from the Discord section and import everyone with access in one click each.'
              : 'Once PR staff add the partner’s team, they appear here.'
          }
        />
      ) : (
        <>
          <div className="hidden md:block table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="th">Member</th>
                  <th className="th">Tier</th>
                  <th className="th">Discord</th>
                  <th className="th">Note</th>
                  {canEdit && <th className="th text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {active.map((member) => (
                  <tr key={member.id}>
                    <td className="td">
                      {who(member)}
                      <p className="text-[11px] text-glass-500 mt-1 flex items-center gap-1 flex-wrap">
                        Added {ago(member.added_at)}
                        {member.added_by && <> by {member.added_by}</>}
                      </p>
                    </td>
                    <td className="td">
                      {canEdit ? (
                        <div className="flex items-center gap-1.5">
                          <TierSelect value={member.tier} onChange={(next) => void changeTier(member, next)} disabled={busyId === member.id} className="glass-button-sm min-h-[32px]" />
                          {busyId === member.id && <Loader className="w-4 h-4 animate-spin text-glass-400" />}
                        </div>
                      ) : (
                        <PartnerTierBadge tier={member.tier} />
                      )}
                    </td>
                    <td className="td">{access(member)}</td>
                    <td className="td max-w-[18rem]">{noteCell(member)}</td>
                    {canEdit && <td className="td">{actions(member)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {active.map((member) => (
              <div key={member.id} className="subcard p-3 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  {who(member)}
                  {actions(member)}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {canEdit ? (
                    <TierSelect value={member.tier} onChange={(next) => void changeTier(member, next)} disabled={busyId === member.id} className="flex-1 min-w-[10rem]" />
                  ) : (
                    <PartnerTierBadge tier={member.tier} />
                  )}
                </div>
                {access(member)}
                {noteCell(member)}
              </div>
            ))}
          </div>
        </>
      )}

      {removed.length > 0 && (
        <details className="subcard p-3">
          <summary className="cursor-pointer text-sm font-medium text-glass-300 min-h-[28px] flex items-center gap-2">
            <History className="w-4 h-4 text-glass-400" />
            Removed from the team ({removed.length})
          </summary>
          <p className="text-xs text-glass-500 mt-2">
            A removed row is what tells Mr. Roboto to take someone’s access and role away, so it stays here after they leave the team. Forget a row
            only once it shows Removed, for example when someone asks for their data to be erased.
          </p>
          <div className="divide-y divide-glass-200/10 mt-2">
            {removed.map((member) => (
              <div key={member.id} className="py-2.5 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[12rem]">{who(member)}</div>
                <PartnerTierBadge tier={member.tier} />
                <PartnerAccessBadge state={member.access_state} detail={member.access_detail} />
                <span className="text-xs text-glass-500 flex items-center gap-1 flex-wrap">
                  Removed {ago(member.removed_at)}
                  {member.removed_by && <> by {member.removed_by}</>}
                </span>
                {canEdit && (
                  <button type="button" className="btn-ghost btn-xs text-red-400 hover:text-red-300" onClick={() => setForgetting(member)} disabled={busyId === member.id}>
                    <Trash2 className="w-3.5 h-3.5" />
                    Forget
                  </button>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      <ConfirmationModal
        isOpen={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => void remove()}
        title={removing ? `Remove ${memberName(removing)} from the team?` : 'Remove from the team?'}
        message={
          managed
            ? 'Mr. Roboto takes away their access to the partner channel, and their partner role unless another partner team they are on still earns it. They stay in the removed history.'
            : 'They stay in the removed history. Discord is not managed for this partner yet, so nothing changes there until it is.'
        }
        confirmText="Remove"
        isLoading={!!removing && busyId === removing.id}
      />

      <ConfirmationModal
        isOpen={!!forgetting}
        onClose={() => setForgetting(null)}
        onConfirm={() => void forget()}
        title={forgetting ? `Forget ${memberName(forgetting)}?` : 'Forget this person?'}
        message={
          forgetting && managed && forgetting.access_state !== 'revoked'
            ? 'Mr. Roboto has not confirmed their access and role are gone yet, and this row is what tells it to take them away. Once forgotten, anything they still hold is only reported as a stray, never removed. Argus deletes their removed rows on this team for good.'
            : 'Argus deletes their removed rows on this team for good and takes their name off this team’s timeline. Do this when someone asks for their data to be erased, after Mr. Roboto has taken their access away.'
        }
        confirmText="Forget"
        isLoading={!!forgetting && busyId === forgetting.id}
      />
    </div>
  );
};
